import React, { useEffect, useState, useCallback } from "react";
import PageHeader from "../components/PageHeader";
import "../styles/OrdersPage.css";
import { fetchOrdersData, fetchInvoicePdf, cancelOnlineOrder } from "../services/api";
import { formatDate } from "../utils/formatDate";

function OrdersPage({ dealer }) {
  const [dispatchData, setDispatchData] = useState([]);
  const [onlineOrders, setOnlineOrders] = useState([]);
  const [lastUpdated, setLastUpdated] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");

  const loadData = useCallback(async () => {
    if (!dealer?.sessionToken) {
      setLoading(false);
      setError("Unable to load data. Tap to retry.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("timeout")), 30000)
      );

      const response = await Promise.race([fetchOrdersData(), timeout]);
      setDispatchData(response.lines || []);
      setOnlineOrders(response.onlineOrders || []);
      setLastUpdated(response.lastUpdated || "");
    } catch (err) {
      setError("Unable to load data. Tap to retry.");
    } finally {
      setLoading(false);
    }
  }, [dealer]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalOrders = dispatchData.length;
  const pendingCount = dispatchData.filter(
    (item) => (item.truePendingQty || 0) > 0
  ).length;
  const materialIssuedCount = dispatchData.filter(
    (item) => (item.materialIssuedQty || 0) > 0
  ).length;
  const dispatchedCount = dispatchData.filter(
    (item) => (item.dispatchedQty || 0) > 0
  ).length;

  const filterDefs = [
    { key: "All", count: totalOrders },
    { key: "Pending", count: pendingCount },
    { key: "Material Issued", count: materialIssuedCount },
    { key: "Dispatched", count: dispatchedCount },
  ];

  const filteredData = dispatchData.filter((item) => {
    switch (activeFilter) {
      case "Pending":
        return (item.truePendingQty || 0) > 0;
      case "Material Issued":
        return (item.materialIssuedQty || 0) > 0;
      case "Dispatched":
        return (item.dispatchedQty || 0) > 0;
      default:
        return true;
    }
  });

  return (
    <div className="orders-page">
      <PageHeader
        title="Order Status"
        actions={
          <button
            type="button"
            className="page-header-refresh"
            onClick={loadData}
            disabled={loading}
            aria-label="Refresh order status"
            title="Refresh data"
          >
            ↻
          </button>
        }
      />

      <main className="orders-content">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <p className="loading-text">Loading your orders…</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <button className="retry-button" onClick={loadData}>
              {error}
            </button>
          </div>
        ) : (
          <>
            {onlineOrders.length > 0 && (
              <section className="online-orders-section" aria-labelledby="online-orders-title">
                <div className="orders-list-header">
                  <div className="online-orders-heading">
                    <h2 id="online-orders-title">Orders placed on portal</h2>
                    <span className="online-orders-count">{onlineOrders.length}</span>
                  </div>
                  <p className="orders-disclaimer">
                    These requests have reached ABN and are awaiting confirmation by our staff.
                  </p>
                </div>
                <div className="orders-card-list">
                  {onlineOrders.map((item) => (
                    <OnlineOrderCard
                      key={item.id || item.orderNumber}
                      item={item}
                      onCancelled={(id) =>
                        setOnlineOrders((orders) => orders.filter((order) => order.id !== id))
                      }
                    />
                  ))}
                </div>
              </section>
            )}

            <div className="orders-filter-field">
              <label htmlFor="orders-filter" className="orders-filter-label">
                Filter
              </label>
              <select
                id="orders-filter"
                className="orders-filter-select"
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
              >
                {filterDefs.map((filter) => (
                  <option key={filter.key} value={filter.key}>
                    {filter.key} ({filter.count})
                  </option>
                ))}
              </select>
            </div>

            <section className="orders-list-section">
              <div className="orders-list-header">
                <h2>Your Dispatch Orders</h2>
                <p className="orders-disclaimer">
                  Showing dispatch data for the last 15 days only.
                  {lastUpdated && ` Last updated: ${lastUpdated}.`}
                </p>
              </div>

              <div className="orders-card-list">
                {filteredData.length === 0 ? (
                  <p>No dispatch records found.</p>
                ) : (
                  filteredData.map((item, index) => (
                    <OrderCard key={item.rowKey ?? index} item={item} />
                  ))
                )}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}


function OnlineOrderCard({ item, onCancelled }) {
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const quantity = Number(item.quantity);
  const availabilityLabels = {
    available: "Available when requested",
    multiple: "Available across locations when requested",
    contact: "Availability confirmation required",
  };

  const handleCancel = async () => {
    if (cancelLoading) return;
    const confirmed = window.confirm(
      `Cancel order request ${item.orderNumber || ""} for ${item.itemNameSnapshot}?`
    );
    if (!confirmed) return;

    setCancelLoading(true);
    setCancelError("");
    try {
      await cancelOnlineOrder(item.id);
      onCancelled?.(item.id);
    } catch (error) {
      setCancelError(error.message || "Unable to cancel this order.");
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <article className="order-card online-order-card">
      <div className="order-card-top">
        <div>
          <div className="online-order-title-row">
            <h3 className="order-card-title">{item.itemNameSnapshot}</h3>
            <span className="online-order-badge">Unrecorded</span>
          </div>
          <p className="order-card-no">
            Request: {item.orderNumber || "—"} · Placed: {formatDate(item.submittedAt) || "—"}
          </p>
        </div>
      </div>

      <div className="order-card-grid">
        <div className="order-field">
          <span className="field-label">Requested Qty</span>
          <span className="field-value">
            {Number.isFinite(quantity) ? quantity.toLocaleString("en-IN") : "—"}
          </span>
        </div>
        <div className="order-field">
          <span className="field-label">Transport</span>
          <span className="field-value">{item.transportMode || "—"}</span>
        </div>
        <div className="order-field">
          <span className="field-label">Status</span>
          <span className="field-value">Received by ABN · awaiting confirmation</span>
        </div>
        <div className="order-field">
          <span className="field-label">Stock check</span>
          <span className="field-value">
            {availabilityLabels[item.availabilityAtOrder] || "Recorded"}
          </span>
        </div>
      </div>

      {item.remarks && (
        <div className="order-details-section">
          <h4 className="order-details-title">Remarks</h4>
          <p className="online-order-remarks">{item.remarks}</p>
        </div>
      )}

      <div className="online-order-actions">
        <button
          type="button"
          className="online-order-cancel"
          onClick={handleCancel}
          disabled={cancelLoading}
        >
          {cancelLoading ? "Cancelling…" : "Cancel order"}
        </button>
        {cancelError && <p className="online-order-cancel-error" role="alert">{cancelError}</p>}
      </div>
    </article>
  );
}

function OrderCard({ item }) {
  const hasPending = (item.truePendingQty || 0) > 0;
  const hasMaterialIssued = (item.materialIssuedQty || 0) > 0;
  const hasDispatched = (item.dispatchedQty || 0) > 0;

  const hasExpectedInfo = item.expectedDate || item.remark;
  const hasDispatchDetails =
    item.materialIssueChallanNo ||
    item.materialIssueChallanDate ||
    item.saleInvoiceNumber ||
    item.saleInvoiceDate ||
    item.saleInvoiceTransport ||
    item.saleInvoiceGrNo;

  const saleInvoiceVchCode =
    item.saleInvoiceVchCode ??
    item.SaleInvoiceVchCode ??
    item.vchCode ??
    item.VchCode ??
    null;

  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState("");

  const handleInvoiceDownload = async () => {
    if (!saleInvoiceVchCode || invoiceLoading) return;

    setInvoiceLoading(true);
    setInvoiceError("");

    try {
      const { blob, filename } = await fetchInvoicePdf(saleInvoiceVchCode);
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || `Invoice-${saleInvoiceVchCode}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      if (err.status === 403) {
        setInvoiceError("This invoice is not available for your account.");
      } else if (err.status === 503) {
        setInvoiceError(
          "Invoice service is temporarily unavailable. Please try again shortly."
        );
      } else {
        setInvoiceError(
          err.message || "Unable to download invoice right now. Please try again in a moment."
        );
      }
    } finally {
      setInvoiceLoading(false);
    }
  };

  return (
    <div className={`order-card${hasPending ? " pending-card" : ""}`}>
      <div className="order-card-top">
        <div>
          <h3 className="order-card-title">{item.itemName}</h3>
          <p className="order-card-no">
            Order No.: {item.orderNo}, Dated: {formatDate(item.orderDate) || "—"}
          </p>
        </div>
      </div>

      <div className="order-card-grid">
        <div className="order-field">
          <span className="field-label">Order Qty</span>
          <span className="field-value">{item.itemOrderQty ?? "—"}</span>
        </div>

        {hasPending && (
          <div className="order-field">
            <span className="field-label">Pending</span>
            <span className="field-value">{item.truePendingQty}</span>
          </div>
        )}

        {hasMaterialIssued && (
          <div className="order-field">
            <span className="field-label">Material Issued (awaiting invoice)</span>
            <span className="field-value">{item.materialIssuedQty}</span>
          </div>
        )}

        {hasDispatched && (
          <div className="order-field">
            <span className="field-label">Dispatched</span>
            <span className="field-value">{item.dispatchedQty}</span>
          </div>
        )}
      </div>

      {hasExpectedInfo && (
        <div className="order-card-grid pending-grid">
          {item.expectedDate && (
            <div className="order-field">
              <span className="field-label">Expected Date</span>
              <span className="field-value">{formatDate(item.expectedDate)}</span>
            </div>
          )}

          {item.remark && (
            <div className="order-field">
              <span className="field-label">Remarks</span>
              <span className="field-value">{item.remark}</span>
            </div>
          )}
        </div>
      )}

      {hasDispatchDetails && (
        <div className="order-details-section">
          <h4 className="order-details-title">Dispatch Details</h4>

          <div className="order-card-grid">
            {item.materialIssueChallanNo && (
              <div className="order-field">
                <span className="field-label">Challan No.</span>
                <span className="field-value">{item.materialIssueChallanNo}</span>
              </div>
            )}

            {item.materialIssueChallanDate && (
              <div className="order-field">
                <span className="field-label">Challan Date</span>
                <span className="field-value">
                  {formatDate(item.materialIssueChallanDate)}
                </span>
              </div>
            )}

            {item.saleInvoiceNumber && (
              <div className="order-field">
                <span className="field-label">Invoice No.</span>

                {saleInvoiceVchCode ? (
                  <button
                    type="button"
                    className="field-value"
                    onClick={handleInvoiceDownload}
                    disabled={invoiceLoading}
                    style={{
                      color: "#9a7b00",
                      textDecoration: "underline",
                      fontWeight: 600,
                      cursor: invoiceLoading ? "default" : "pointer",
                      background: "none",
                      border: "none",
                      padding: 0,
                      font: "inherit",
                    }}
                    title="Download invoice PDF"
                  >
                    {invoiceLoading ? "Downloading…" : item.saleInvoiceNumber}
                  </button>
                ) : (
                  <span className="field-value">{item.saleInvoiceNumber}</span>
                )}

                {invoiceError && (
                  <p
                    style={{
                      color: "#c0392b",
                      fontSize: "0.85em",
                      marginTop: "4px",
                    }}
                  >
                    {invoiceError}
                  </p>
                )}
              </div>
            )}

            {item.saleInvoiceDate && (
              <div className="order-field">
                <span className="field-label">Invoice Date</span>
                <span className="field-value">{formatDate(item.saleInvoiceDate)}</span>
              </div>
            )}

            {item.saleInvoiceTransport && (
              <div className="order-field">
                <span className="field-label">Transport</span>
                <span className="field-value">{item.saleInvoiceTransport}</span>
              </div>
            )}

            {item.saleInvoiceGrNo && (
              <div className="order-field">
                <span className="field-label">GR No.</span>
                <span className="field-value">{item.saleInvoiceGrNo}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default OrdersPage;
